import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { reduxForm, getFormValues } from 'redux-form';
import PropTypes from 'prop-types';
import { findIndex, isEmpty } from 'lodash';
import { ADD_SEQUENCE_STEPS, ADD_SEQUENCE_STEPPER_STEPS, DEFAULT_SEQUENCE_NAME } from 'constants/sequence';
import { getSequenceContacts } from 'redux/actions/sequenceContactActions';
import ClickToEdit from 'components/forms/EditInline';
import SequenceSchedule from 'components/modals/SequenceModal/SequenceSchedule';
import SequenceSummary from 'components/modals/SequenceModal/SequenceSummary';
import { destroyAccountContacts, resetPreviewContacts } from 'redux/actions/contactActions';
import LoadingIcon from 'mdi-react/LoadingIcon';
import { Grid, Paper, withStyles } from '@material-ui/core';
import logo from 'images/logo/logo.png';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Stepper from 'components/Stepper';
import { updateSequence } from 'redux/actions/sequenceActions';
import {
  ContactsStartPage,
  PitchesPage,
  ContactsPreview,
} from 'components/modals/SequenceModal';
import ConfirmationModal from 'components/modals/ConfirmationModal';
import classNames from 'classnames';


class SequenceSteps extends Component {
  static propTypes = {
    classes: PropTypes.shape({}).isRequired,
    toggleModal: PropTypes.func.isRequired,
    closeSequenceModal: PropTypes.func.isRequired,
    sequence: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
    }).isRequired,
    campaign: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }).isRequired,
    contacts: PropTypes.shape({
      loading: PropTypes.bool,
    }).isRequired,
    destroyAccountContacts: PropTypes.func.isRequired,
    sequencePreviewContacts: PropTypes.shape({
      total_valid_contacts: PropTypes.number,
    }).isRequired,
    launchSequence: PropTypes.func.isRequired,
    updateSequence: PropTypes.func.isRequired,
    resetPreviewContacts: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.setStep = this.setStep.bind(this);
    this.state = {
      step: ADD_SEQUENCE_STEPS.CONTACTS_START_STEP,
      skippedSteps: new Set(),
      openContactRemoveConfirmation: false,
    };
    this.setStep = this.setStep.bind(this);
    this.handleNameEdit = this.handleNameEdit.bind(this);
    this.goToContactsStartPage = this.goToContactsStartPage.bind(this);
    this.toggleContactRemoveConfirmation = this.toggleContactRemoveConfirmation.bind(this);
    this.confirmContactRemove = this.confirmContactRemove.bind(this);
  }

  componentWillUnmount() {
    this.props.resetPreviewContacts();
  }

  setStep(step) {
    this.setState({ step });
  }

  getActiveStepIndex = () => {
    const activeStep = this.state.step === ADD_SEQUENCE_STEPS.CONTACTS_PREVIEW_STEP ?
      ADD_SEQUENCE_STEPS.CONTACTS_START_STEP : this.state.step;
    return findIndex(ADD_SEQUENCE_STEPPER_STEPS, ['value', activeStep]);
  }

  toggleContactRemoveConfirmation() {
    this.setState({ openContactRemoveConfirmation: !this.state.openContactRemoveConfirmation });
  }

  async confirmContactRemove() {
    this.toggleContactRemoveConfirmation();
    await this.deleteContacts();
    this.changeStep(ADD_SEQUENCE_STEPS.CONTACTS_START_STEP);
  }

  skipStep(nextStep) {
    this.setState((state) => {
      const newSkippedSteps = new Set(state.skippedSteps.values());
      newSkippedSteps.add(this.getActiveStepIndex());
      return { step: nextStep, skippedSteps: newSkippedSteps };
    });
  }

  changeStep(nextStep) {
    this.setState((state) => {
      const newSkippedSteps = new Set(state.skippedSteps.values());
      newSkippedSteps.delete(this.getActiveStepIndex());
      return { step: nextStep, skippedSteps: newSkippedSteps };
    });
  }

  goToContactsStartPage() {
    const { sequencePreviewContacts } = this.props;
    if (!isEmpty(sequencePreviewContacts.results)) {
      this.toggleContactRemoveConfirmation();
    } else {
      this.changeStep(ADD_SEQUENCE_STEPS.CONTACTS_START_STEP);
    }
  }

  async skipContactStep() {
    await this.props.resetPreviewContacts();
    this.skipStep(ADD_SEQUENCE_STEPS.PITCHES_STEP);
  }

  deleteContacts = async () => {
    const { sequence, sequencePreviewContacts } = this.props;
    await this.props.destroyAccountContacts(
      sequence.account_id,
      {
        contact_ids: sequencePreviewContacts.results.filter(obj => isEmpty(obj.errors)).map(obj => obj.id),
        sequence_ids: [sequence.id],
      },
    );
    await this.props.resetPreviewContacts();
  }

  handleNameEdit(sequenceName) {
    if (sequenceName) {
      this.props.updateSequence(this.props.sequence.id, { name: sequenceName }, true);
    }
  }

  render() {
    const {
      closeSequenceModal,
      launchSequence,
      toggleModal,
      sequence,
      contacts,
      classes,
    } = this.props;
    const { step, skippedSteps, openContactRemoveConfirmation } = this.state;
    return (
      <>
        {contacts.loading && <div className="loader"><LoadingIcon /></div>}
        <ConfirmationModal
          open={openContactRemoveConfirmation}
          title="Remove Contacts"
          confirmLabel="Remove"
          size="medium"
          divider={false}
          onCancelClick={this.toggleContactRemoveConfirmation}
          onBackdropClick={this.toggleContactRemoveConfirmation}
          onConfirmClick={this.confirmContactRemove}
          classNames={{ title: classes.dialogTitle, content: classes.dialogContent }}
        >
          Are you sure you want to remove all contacts from your sequence?
        </ConfirmationModal>
        <Grid container>
          <Grid item md={3} className="sequence-create-sidebar">
            <div className="logo">
              <img src={logo} width="156" alt="prvolt logo" />
            </div>
            <Stepper
              className="shadow-none"
              skippedSteps={skippedSteps}
              activeStep={this.getActiveStepIndex()}
              orientation="vertical"
              steps={ADD_SEQUENCE_STEPPER_STEPS}
            />
          </Grid>
          <Grid item md={9}>
            <div className="sequence-create builder p-5">
              {sequence.loading && <div className="loader"><LoadingIcon /></div>}
              <div className="flex justify-content-between">
                <ClickToEdit
                  editIcon
                  wrapperClass="page-title"
                  textClass={classNames({ 'title-name': sequence.name === DEFAULT_SEQUENCE_NAME })}
                  value={sequence.name}
                  endEditing={this.handleNameEdit}
                />
                <IconButton aria-label="close" onClick={toggleModal} className="mb-3">
                  <CloseIcon />
                </IconButton>
              </div>
              <Paper disableTypography>
                <div className="p-4">
                  {step === ADD_SEQUENCE_STEPS.CONTACTS_START_STEP && (
                    <ContactsStartPage
                      sequenceBuilder
                      setStep={this.setStep}
                      onSkip={() => this.skipContactStep()}
                      onNext={() => this.setStep(ADD_SEQUENCE_STEPS.CONTACTS_PREVIEW_STEP)}
                      toggleModal={closeSequenceModal}
                    />
                  )}
                  {step === ADD_SEQUENCE_STEPS.CONTACTS_PREVIEW_STEP && (
                    <ContactsPreview
                      sequenceBuilder
                      setStep={this.setStep}
                      onBack={this.goToContactsStartPage}
                      onSkip={() => this.skipContactStep()}
                      onNext={() => this.changeStep(ADD_SEQUENCE_STEPS.PITCHES_STEP)}
                      onSubmit={closeSequenceModal}
                    />
                  )}
                  {step === ADD_SEQUENCE_STEPS.PITCHES_STEP && (
                    <PitchesPage
                      setStep={this.setStep}
                      onBack={this.goToContactsStartPage}
                      onSkip={() => this.skipStep(ADD_SEQUENCE_STEPS.SCHEDULE_STEP)}
                      onNext={() => this.changeStep(ADD_SEQUENCE_STEPS.SCHEDULE_STEP)}
                      onSave={closeSequenceModal}
                    />
                  )}
                  {step === ADD_SEQUENCE_STEPS.SCHEDULE_STEP && (
                    <SequenceSchedule
                      onBack={() => this.changeStep(ADD_SEQUENCE_STEPS.PITCHES_STEP)}
                      onNext={() => this.changeStep(ADD_SEQUENCE_STEPS.SUMMARY_STEP)}
                      onSave={closeSequenceModal}
                    />
                  )}
                  {step === ADD_SEQUENCE_STEPS.SUMMARY_STEP && (
                    <SequenceSummary
                      onBack={() => this.changeStep(ADD_SEQUENCE_STEPS.SCHEDULE_STEP)}
                      onSave={closeSequenceModal}
                      onDelete={toggleModal}
                      onNext={launchSequence}
                      setStep={this.setStep}
                    />
                  )}
                </div>
              </Paper>
            </div>
          </Grid>
        </Grid>
      </>
    );
  }
}

const styles = () => ({
  dialogTitle: {
    justifyContent: 'center',
    fontSize: '1.5rem !important',
  },
  dialogContent: {
    fontSize: '1rem !important',
  },
});

export default compose(
  withStyles(styles),
  connect(
    state => ({
      sequence: state.sequence,
      formValues: getFormValues('sequenceCreateBasicForm')(state),
      sequencePreviewContacts: state.previewContacts,
      contacts: state.contacts,
    }),
    {
      getSequenceContacts,
      updateSequence,
      destroyAccountContacts,
      resetPreviewContacts,
    },
  ),
  reduxForm({
    form: 'sequenceCreateBasicForm',
  }),
)(SequenceSteps);
